<template>
  <div>
    <div v-if="showQuestions" class="row">
      <div class="col-md-10 col-lg-8 col-xl-6 mx-auto header">
        <h2 class="skin-page-heading">Providers</h2>
        <p class="question">Tell us what is most important to you for choosing a provider to use in retirement</p>

        <div class="answers">
          <div
            v-for="(answer, i) in answers"
            v-bind:key="i"
            class="form-check shadow-sm d-flex align-self-center"
          >
            <input
              class="form-check-input d-flex align-self-center"
              type="radio"
              :name="`flexRadioDefault${questionId}`"
              :id="`flexRadioDefault${questionId}${i}`"
              :value="i"
              v-model="questionResponses[questionId]"
            />
            <label
              class="form-check-label"
              :for="`flexRadioDefault${questionId}${i}`"
            >
              {{ answer }}
            </label>
          </div>
        </div>

        <p>
          <button
            type="button"
            class="btn btn-primary"
            @click="onContinueClick"
          >
            Continue
          </button>
        </p>
      </div>
    </div>

    <div v-if="showProviders" class="row">
      <div class="col-xl-9 mx-auto header">
        <low-cost-drawdown v-if="outcome == Outcome.LowCostDrawdown" />

        <simple-comparison v-if="outcome == Outcome.SimpleComparison" />

        <div v-if="showReferralSmallPrint" class="text-start mt-4">
          <p class="text-muted">
            <small>
              Disclaimer - We do not take any referral fees from any providers above. Some 
              providers may license our tools for their own use, so we do have commercial 
              relationships. You can seek other providers on your own or get independent 
              financial advice to find the most appropriate. You can do this via our Advisers 
              page, or go to <a href="https://www.moneyhelper.org.uk" target="_blank">moneyhelper.org.uk</a> if 
              you want more help on finding an adviser.
            </small>
          </p>
        </div>

        <div v-if="showBackButton">
          <p class="text-start">
            <a
              href=""
              class="btn btn-outline-dark"
              @click="onBackToQuestionsClick($event)"
            >
              <i class="bi bi-arrow-left"></i>
              Back
            </a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LowCostDrawdown from "@/guiide/components/guiideplus/providers/LowCostDrawdown.vue";
import SimpleComparison from "@/guiide/components/guiideplus/providers/SimpleComparison.vue";
import { Configuration } from "@/guiide/ts/configuration/configuration";
import { guiidePlusMonitoring } from "@/guiide/ts/guiideplus/guiidePlusMonitoring";

enum ActiveSection {
  Questions,
  Providers
}

enum Outcome {
  NoOutcome,
  LowCostDrawdown,
  SimpleComparison
}

export default defineComponent({
  name: "Providers",
  emits: ["redirect-required"],
  components: {
    LowCostDrawdown,
    SimpleComparison
  },
  data() {
    return {
      initialLoad: true,
      activeSection: ActiveSection.Questions,
      questionId: 1,
      questionResponses: [],
      outcome: Outcome.NoOutcome,
      showBackButton: true,
      Outcome
    };
  },
  mounted() {
    if (this.initialLoad) {
      const config: Configuration = this.$store.state.configuration;
      this.initialLoad = false;
    }
  },
  computed: {
    showQuestions: function(): boolean {
      return this.activeSection == ActiveSection.Questions;
    },
    showProviders: function(): boolean {
      return this.activeSection == ActiveSection.Providers;
    },
    answers: function(): Array<string> {
      switch (this.questionId) {
        case 1:
          return [
            "One that is specifically designed to help make my Guiide plan work as simply as possible",
            "Just a low cost simple provider with clear competitive charges"
          ];
        default:
          return new Array<string>();
      }
    },
    showReferralSmallPrint: function(): boolean {
      return (
        this.questionId > 0 &&
        (this.outcome == Outcome.LowCostDrawdown)
      );
    }
  },
  methods: {
    reset() {
      this.activeSection = ActiveSection.Questions;
      this.questionId = 1;
      this.questionResponses = [];
      this.outcome = Outcome.NoOutcome;
      this.showBackButton = true;
    },
    showProvidersView() {
      this.setView(Outcome.LowCostDrawdown);
    },
    showSimpleComparisonView() {
      this.setView(Outcome.SimpleComparison);
    },
    setView(outcome: Outcome) {
      this.questionId = 1;
      this.questionResposes = [0, 0];
      this.activeSection = ActiveSection.Providers;
      this.outcome = outcome;
      this.showBackButton = false;
    },
    onContinueClick() {
      if (this.questionResponses.length < this.questionId + 1) {
        return;
      }

      const answerId = this.questionResponses[this.questionId];

      switch (this.questionId) {
        case 1:
          switch (answerId) {
            case 1:
              this.outcome = Outcome.LowCostDrawdown;
              this.activeSection = ActiveSection.Providers;
              guiidePlusMonitoring.logLowCostProviders();
              return;
          }
          break;
      }
    },
    onBackToQuestionsClick(event: any) {
      event.preventDefault();
      this.activeSection = ActiveSection.Questions;
    }
  }
});
</script>

<style scoped>
.header {
  text-align: center;
}

.h2 {
  display: inline-block;
  padding: 0 30px 15px;
  border-bottom: 1px solid #ccc;
}

.question {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 25px;
}

.answers {
  margin: 45px 0;
}

.answers .form-check {
  background-color: #fff;
  padding-left: 0;
  text-align: left;
  border-radius: 8px;
  margin: 16px 0;
  border: 1px solid transparent;
}

.answers .form-check:hover {
  border: 1px solid #00497f;
}

.answers .form-check-input {
  margin: 20px;
  padding: 10px;
}

.answers label {
  display: block;
  padding: 20px;
  cursor: pointer;
}

.btn-primary {
  padding: 10px 45px;
  border-radius: 45px;
  background-color: #3e566e !important;
  border-color: #3e566e !important;
  color: #fff;
}
</style>
